import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Skeleton,
  V2ToastStack,
  type V2ToastItem,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, apiPatch } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { Todo, TodoListResponse } from '../../lib/types/projects.js';
import './project-lists.css';

const OPEN_LABEL = 'باز';
const TOAST_MS = 5000;

const fa = (n: number) => n.toLocaleString('fa-IR');

/**
 * UI_REFERENCE_SOURCE: no dedicated Design Board — follows the design
 * system's own primitives directly, per the route inventory's PO decision.
 * canonical pattern: inline quick-add + checkable list (open first, closed below)
 */
export function TodosPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const res = useApiGet<TodoListResponse>(slug ? `${base}/api/v1/projects/todos` : null, [slug]);

  // Local copy so a check-off shows instantly and the list stays visible on reload.
  const [todos, setTodos] = useState<readonly Todo[] | null>(null);
  const fetched = res.status === 'ready' ? res.data.todos : null;
  useEffect(() => {
    if (fetched) setTodos(fetched);
  }, [fetched]);

  const [toasts, setToasts] = useState<readonly V2ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: string) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  const toast = useCallback(
    (message: string, tone: NonNullable<V2ToastItem['tone']>) => {
      seq.current += 1;
      const id = `t${seq.current}`;
      setToasts((p) => [...p, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), TOAST_MS);
    },
    [dismiss],
  );

  if (!slug) return null;

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    const text = title.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/projects/todos`,
        { title: text },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setTitle('');
      res.reload();
    } catch {
      toast('ثبت یادداشت ناموفق بود.', 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  async function onStatus(todo: Todo, status: 'done' | 'cancelled') {
    if (pending.has(todo.ref)) return;
    const closedLabel = status === 'done' ? 'انجام‌شده' : 'لغو شده';
    const setLabel = (label: string) =>
      setTodos((prev) =>
        prev ? prev.map((t) => (t.ref === todo.ref ? { ...t, statusLabel: label } : t)) : prev,
      );
    const setBusy = (busy: boolean) =>
      setPending((prev) => {
        const next = new Set(prev);
        if (busy) next.add(todo.ref);
        else next.delete(todo.ref);
        return next;
      });

    setLabel(closedLabel);
    setBusy(true);
    try {
      await apiPatch(`${base}/api/v1/projects/todos/${encodeURIComponent(todo.ref)}`, { status });
    } catch {
      setLabel(todo.statusLabel);
      toast('تغییر وضعیت انجام نشد؛ دوباره تلاش کنید.', 'danger');
    } finally {
      setBusy(false);
    }
  }

  const open = (todos ?? []).filter((t) => t.statusLabel === OPEN_LABEL);
  const closed = (todos ?? []).filter((t) => t.statusLabel !== OPEN_LABEL);

  function renderRow(todo: Todo) {
    const isOpen = todo.statusLabel === OPEN_LABEL;
    return (
      <div
        key={todo.ref}
        className={`prj-todos__row${isOpen ? '' : ' is-closed'}${pending.has(todo.ref) ? ' is-pending' : ''}`}
      >
        <input
          className="sk-check"
          type="checkbox"
          checked={!isOpen}
          disabled={!isOpen || pending.has(todo.ref)}
          aria-label={`انجام شد: ${todo.title}`}
          onChange={() => void onStatus(todo, 'done')}
        />
        <span className="prj-todos__title">{todo.title}</span>
        {isOpen ? (
          <V2Button
            size="sm"
            variant="ghost"
            iconOnly
            icon="x"
            aria-label={`لغو: ${todo.title}`}
            title="لغو یادداشت"
            disabled={pending.has(todo.ref)}
            onClick={() => void onStatus(todo, 'cancelled')}
          />
        ) : (
          <V2Badge tone="neutral" plain>
            {todo.statusLabel}
          </V2Badge>
        )}
      </div>
    );
  }

  let body;
  if (todos === null && res.status === 'error') {
    body = (
      <StatePanel
        variant={res.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={res.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
      />
    );
  } else if (todos === null) {
    body = (
      <div aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className="prj-todos__skeleton-row">
            <V2Skeleton width={18} height={18} />
            <V2Skeleton height={12} />
          </div>
        ))}
      </div>
    );
  } else if (todos.length === 0) {
    body = (
      <V2EmptyState
        compact
        icon="tasks"
        title="هنوز یادداشتی ندارید."
        description="هر کاری به ذهنتان رسید، همین بالا بنویسید و Enter بزنید."
      />
    );
  } else {
    body = (
      <>
        {open.length === 0 ? (
          <V2EmptyState compact icon="check" title="همهٔ یادداشت‌ها انجام شده است." />
        ) : (
          open.map(renderRow)
        )}
        {closed.length > 0 ? (
          <>
            <div className="prj-todos__section-label">بسته‌شده ({fa(closed.length)})</div>
            {closed.map(renderRow)}
          </>
        ) : null}
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card clipped>
        <V2CardHeader
          icon="check"
          title="کارهای شخصی"
          subtitle={todos ? `${fa(open.length)} مورد باز` : undefined}
        />
        <form className="prj-todos__add" onSubmit={onCreate}>
          <input
            className="sk-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="یادداشت جدید… (Enter برای ثبت)"
            aria-label="عنوان یادداشت جدید"
          />
          <V2Button
            type="submit"
            variant="primary"
            icon="plus"
            disabled={submitting || title.trim() === ''}
          >
            {submitting ? 'در حال ثبت…' : 'افزودن'}
          </V2Button>
        </form>
        {body}
      </V2Card>
      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
